// Cálculo de períodos y saldo de vacaciones según fecha de ingreso del empleado.
// CR (Código de Trabajo Art. 153): 2 semanas por 50 semanas de trabajo.
// Total anual según actividad económica de la empresa:
//   - Comercio, Hotelería y similares → 14 días por período
//   - Servicios, Industria y similares → 12 días por período
//
// Progresión (idéntica hasta el mes 11):
//   - Meses 1..11 → 1 día por mes cumplido (= 11 días al cumplir 11 meses)
//   - Medio mes 11 → 11.5 (aprox 50 semanas) → salto al total anual (12 o 14)
//
// Modelo:
//   - Período N = [fechaIngreso + (N-1) año, fechaIngreso + N año − 1 día]
//   - Días generados: períodos completados × total anual + progresión del actual
//   - Días disfrutados = registros de Asistencia con estado="Vacaciones"
//   - Saldo acumulado = generados − disfrutados

const DIAS_ANUAL_DEFAULT = 12;
const SEMANAS_VENCIMIENTO = 15; // Art. 155 CCT — vacaciones deben tomarse dentro de las 15 semanas siguientes
const DIAS_VENCIMIENTO = SEMANAS_VENCIMIENTO * 7;

// Devuelve el total de días de vacaciones anuales según la actividad económica.
export function diasVacacionesAnual(empresa) {
  const grupo = empresa?.actividad_grupo;
  if (grupo === "comercio_hoteleria") return 14;
  return 12; // servicios_industria y default
}

// Días acumulados según meses cumplidos y total anual, con la regla del "medio mes final".
// - meses < 11        → 1 día por mes cumplido
// - 11 ≤ meses < 11.5 → 11 días (mantiene, aún no llega al medio mes final)
// - meses ≥ 11.5      → totalAnual (12 o 14)
function diasAcumuladosEnPeriodo(mesesCumplidos, diaExtraMedioMes, totalAnual) {
  if (mesesCumplidos >= 12) return totalAnual;
  if (diaExtraMedioMes) return totalAnual;
  if (mesesCumplidos >= 11) return 11;
  return Math.max(0, Math.floor(mesesCumplidos));
}

function parseDate(d) {
  if (!d) return null;
  if (d instanceof Date) return new Date(d);
  return new Date(`${d.slice(0,10)}T00:00:00`);
}

function toISO(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function monthsBetween(start, end) {
  // Meses completos transcurridos desde 'start' hasta 'end'
  let m = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
  if (end.getDate() < start.getDate()) m -= 1;
  return Math.max(0, m);
}

export function getPeriodos(fechaIngreso, hasta = new Date()) {
  const ing = parseDate(fechaIngreso);
  if (!ing) return [];
  const target = hasta instanceof Date ? hasta : parseDate(hasta);
  const periodos = [];
  let idx = 1;
  let start = new Date(ing);
  while (start <= target || periodos.length === 0) {
    const end = new Date(start);
    end.setFullYear(end.getFullYear() + 1);
    end.setDate(end.getDate() - 1);
    periodos.push({
      idx,
      start: toISO(start),
      end: toISO(end),
      label: `${start.getFullYear()}-${end.getFullYear()}`,
    });
    if (end >= target) break;
    start = new Date(end);
    start.setDate(start.getDate() + 1);
    idx++;
    if (idx > 80) break;
  }
  return periodos;
}

// Semanas completas transcurridas entre 2 fechas (Date)
function weeksBetween(start, end) {
  const ms = end.getTime() - start.getTime();
  return Math.floor(ms / (7 * 86400 * 1000));
}

// Cálculo de "generados" a una fecha específica, según total anual (12 o 14).
function generadosHastaConTotal(fechaIngreso, upto, totalAnual) {
  const periodos = getPeriodos(fechaIngreso, upto);
  if (periodos.length === 0) return 0;
  const t = new Date(upto); t.setHours(0,0,0,0);
  let g = 0;
  for (const p of periodos) {
    const pStart = parseDate(p.start);
    const pEnd = parseDate(p.end);
    if (t > pEnd) {
      // Período cerrado → total anual completo
      g += totalAnual;
    } else if (t >= pStart) {
      // Período en curso → aplicar progresión:
      //  · meses cumplidos: 1 día por mes hasta el mes 11 (= 11 días)
      //  · al llegar a 50 semanas (medio mes final ~ 11.5) → salto al totalAnual
      const meses = monthsBetween(pStart, t);
      const semanas = weeksBetween(pStart, t);
      const diaExtraMedioMes = semanas >= 50;
      g += diasAcumuladosEnPeriodo(meses, diaExtraMedioMes, totalAnual);
    }
  }
  return g;
}

// Wrapper con valor por defecto (12) para retrocompat.
function generadosHasta(fechaIngreso, upto, totalAnual = DIAS_ANUAL_DEFAULT) {
  return generadosHastaConTotal(fechaIngreso, upto, totalAnual);
}

export function calcularSaldoVacaciones(empleado, asistencias = [], today = new Date(), empresa = null) {
  const def = { generados: 0, disfrutados: 0, saldo: 0, periodoActual: null, periodos: [], proximoAniversario: null, diasPorVencer: 0, diasVencidos: 0, vencimientoProximo: null, estadoVencimiento: "ninguno", diasAnual: DIAS_ANUAL_DEFAULT };
  if (!empleado || !empleado.fecha_ingreso) return def;
  const totalAnual = diasVacacionesAnual(empresa);
  const t = new Date(today); t.setHours(0,0,0,0);
  const periodos = getPeriodos(empleado.fecha_ingreso, t);
  if (periodos.length === 0) return def;

  // Los disfrutados se cuentan SIEMPRE completos (todas las vacaciones registradas
  // en asistencia). Para "Pagadas" un único registro puede representar N días.
  const disfrutados = (asistencias || [])
    .filter(a => a.empleado_id === empleado.id && a.estado === "Vacaciones")
    .reduce((sum, a) => {
      const esPagadas = (a.tipo_vacacion || "Disfrutadas") === "Pagadas";
      if (esPagadas && Number(a.dias_pagados) > 0) return sum + Number(a.dias_pagados);
      return sum + 1;
    }, 0);

  const generados = generadosHastaConTotal(empleado.fecha_ingreso, t, totalAnual);

  // Recorrer períodos para exponer el "período actual" (info para la UI)
  const periodosCompletos = [];
  let periodoActual = null;
  for (const p of periodos) {
    const pStart = parseDate(p.start);
    const pEnd = parseDate(p.end);
    if (t > pEnd) {
      periodosCompletos.push({ ...p, dias: totalAnual });
    } else if (t >= pStart) {
      periodoActual = p;
    }
  }

  // Exposición clásica: "Período Actual" = último cerrado, "Período Nuevo" = en curso.
  const periodoActualCerrado = periodosCompletos.length > 0 ? periodosCompletos[periodosCompletos.length - 1] : null;
  const diasPeriodoActual = periodoActualCerrado ? periodoActualCerrado.dias : 0;
  const diasPeriodoNuevo = periodoActual
    ? (function () {
        const pStart = parseDate(periodoActual.start);
        const meses = monthsBetween(pStart, t);
        const semanas = weeksBetween(pStart, t);
        return diasAcumuladosEnPeriodo(meses, semanas >= 50, totalAnual);
      })()
    : 0;
  const labelPeriodoActual = periodoActualCerrado ? periodoActualCerrado.label : (periodoActual ? periodoActual.label : null);
  const labelPeriodoNuevo = periodoActualCerrado && periodoActual ? periodoActual.label : null;

  // Vencidas: días de períodos completados ANTERIORES al "actual" cuya venc ya pasó.
  const periodosVencidos = periodosCompletos.slice(0, -1).filter(p => {
    const venc = parseDate(p.end); venc.setDate(venc.getDate() + DIAS_VENCIMIENTO);
    return venc < t;
  });
  const diasVencidosTotal = periodosVencidos.reduce((s, p) => s + p.dias, 0);

  // FIFO: disfrutados consumen primero vencidos, luego actual, luego nuevo.
  let restantes = disfrutados;
  const consumidosVencidos = Math.min(diasVencidosTotal, restantes);
  restantes -= consumidosVencidos;
  const diasVencidasDisponibles = Math.max(0, diasVencidosTotal - consumidosVencidos);

  const consumidosActual = Math.min(diasPeriodoActual, restantes);
  restantes -= consumidosActual;
  const diasActualDisponibles = Math.max(0, diasPeriodoActual - consumidosActual);

  const consumidosNuevo = Math.min(diasPeriodoNuevo, restantes);
  restantes -= consumidosNuevo;
  const diasNuevoDisponibles = Math.max(0, diasPeriodoNuevo - consumidosNuevo);

  const saldo = diasActualDisponibles + diasNuevoDisponibles;

  // Estado del período "actual" para alerta de vencimiento próximo (dentro de 15 sem)
  let vencimientoProximo = null;
  let estadoVencimiento = "ninguno";
  if (diasVencidasDisponibles > 0) {
    estadoVencimiento = "vencido";
    const ultimoVencido = periodosVencidos[periodosVencidos.length - 1];
    if (ultimoVencido) {
      const v = parseDate(ultimoVencido.end); v.setDate(v.getDate() + DIAS_VENCIMIENTO);
      vencimientoProximo = v;
    }
  } else if (periodoActualCerrado && diasActualDisponibles > 0) {
    const v = parseDate(periodoActualCerrado.end); v.setDate(v.getDate() + DIAS_VENCIMIENTO);
    const diasParaVencer = Math.ceil((v - t) / (1000 * 60 * 60 * 24));
    if (diasParaVencer <= 28) {
      estadoVencimiento = "proximo";
      vencimientoProximo = v;
    } else {
      estadoVencimiento = "seguro";
      vencimientoProximo = v;
    }
  }

  const proximoAniversario = periodoActual ? periodoActual.end : (periodos[periodos.length - 1]?.end || null);
  return {
    generados, disfrutados, saldo, periodoActual, periodos, proximoAniversario,
    diasPorVencer: diasActualDisponibles, diasVencidos: diasVencidasDisponibles,
    vencimientoProximo: vencimientoProximo ? toISO(vencimientoProximo) : null,
    estadoVencimiento, diasAnual: totalAnual,
    diasPeriodoActual: diasActualDisponibles,
    diasPeriodoNuevo: diasNuevoDisponibles,
    diasVencidas: diasVencidasDisponibles,
    labelPeriodoActual,
    labelPeriodoNuevo,
    periodoActualCerrado,
  };
}

// Calcula la vista detallada período-por-período para la pestaña Vacaciones del empleado.
// Devuelve: { periodos: [{label, start, end, generados, disfrutados, saldo, esActual, esFuturo}], saldoTotal, diasAnual }
// Reglas:
//   - Un período se muestra sólo si su fecha de inicio ya pasó (start <= hoy).
//   - Generados de un período cerrado = totalAnual (12 o 14). Del período en curso = meses cumplidos.
//   - Disfrutados = suma de días de asistencia con estado="Vacaciones" cuya fecha cae en [start, end].
//     Para tipo="Pagadas" cuenta el campo dias_pagados.
//   - Saldo por período = Generados − Disfrutados. Si es negativo (disfrutó de más), queda negativo.
//   - El saldo del período MÁS RECIENTE incluye el arrastre de saldos positivos de períodos anteriores.
//   - El "Saldo de Vacaciones" del empleado = saldo del período más reciente (con arrastres).
export function calcularPeriodosDetallados(empleado, asistencias = [], today = new Date(), empresa = null) {
  if (!empleado || !empleado.fecha_ingreso) return { periodos: [], saldoTotal: 0, diasAnual: DIAS_ANUAL_DEFAULT };
  const totalAnual = diasVacacionesAnual(empresa);
  const t = new Date(today); t.setHours(0, 0, 0, 0);
  const todos = getPeriodos(empleado.fecha_ingreso, t);
  const visibles = todos.filter(p => parseDate(p.start) <= t);
  if (visibles.length === 0) return { periodos: [], saldoTotal: 0, diasAnual: totalAnual };

  // Registros de vacaciones ya disfrutadas
  const vacaciones = (asistencias || [])
    .filter(a => a.empleado_id === empleado.id && a.estado === "Vacaciones");

  const periodosDetallados = visibles.map((p, idx) => {
    const pStart = parseDate(p.start);
    const pEnd = parseDate(p.end);
    const esActual = t >= pStart && t <= pEnd;
    // Generados AUTO
    let generadosAuto;
    if (esActual) {
      const meses = monthsBetween(pStart, t);
      const semanas = weeksBetween(pStart, t);
      generadosAuto = diasAcumuladosEnPeriodo(meses, semanas >= 50, totalAnual);
    } else {
      generadosAuto = totalAnual;
    }
    // Rebajos manuales: reducen los generados del período. Se almacenan en `vacaciones_ajustes`.
    const ajustes = Array.isArray(empleado.vacaciones_ajustes) ? empleado.vacaciones_ajustes : [];
    const rebajosDelPeriodo = ajustes.filter(a => a.periodo_label === p.label);
    const totalRebajado = rebajosDelPeriodo.reduce((s, a) => s + (Number(a.dias) || 0), 0);
    const generados = Math.max(0, generadosAuto - totalRebajado);
    // Disfrutados AUTOMÁTICOS: días de vacaciones que caen dentro del rango [start, end]
    const disfrutadosAuto = vacaciones
      .filter(a => {
        if (!a.fecha) return false;
        const f = parseDate(a.fecha);
        return f >= pStart && f <= pEnd;
      })
      .reduce((sum, a) => {
        const esPagadas = (a.tipo_vacacion || "Disfrutadas") === "Pagadas";
        if (esPagadas && Number(a.dias_pagados) > 0) return sum + Number(a.dias_pagados);
        return sum + 1;
      }, 0);
    // Si el patrono configuró un override manual por período, se usa ese valor.
    const overrides = empleado.vacaciones_overrides || {};
    const override = overrides[p.label];
    const tieneOverride = override && override.disfrutados != null && override.disfrutados !== "";
    const disfrutados = tieneOverride ? Number(override.disfrutados) : disfrutadosAuto;
    return {
      label: p.label,
      start: p.start,
      end: p.end,
      generados,
      generadosAuto,
      totalRebajado,
      rebajos: rebajosDelPeriodo,
      disfrutados,
      disfrutadosAuto,
      tieneOverride,
      saldo: generados - disfrutados,
      esActual,
      esUltimo: false,
    };
  });

  // Marcar el último período con `esUltimo`.
  // Definiciones:
  //   - saldoTotal   = suma de saldos de TODOS los períodos (incluye el en curso)
  //   - saldoVencidas = suma de saldos de períodos ya CERRADOS (no incluye el en curso)
  const nPeriodos = periodosDetallados.length;
  if (nPeriodos > 0) periodosDetallados[nPeriodos - 1].esUltimo = true;
  const saldoTotal = periodosDetallados.reduce((s, p) => s + p.saldo, 0);
  const saldoVencidas = periodosDetallados.filter(p => !p.esActual).reduce((s, p) => s + p.saldo, 0);
  return { periodos: periodosDetallados, saldoTotal, saldoVencidas, diasAnual: totalAnual };
}

// Sumar días entre 2 fechas (inclusivo). Solo días naturales (sin filtrar fines de semana).
export function diasEntre(fechaInicio, fechaFin) {
  const a = parseDate(fechaInicio);
  const b = parseDate(fechaFin);
  if (!a || !b) return 0;
  const ms = b.getTime() - a.getTime();
  return Math.max(0, Math.round(ms / (1000 * 60 * 60 * 24)) + 1);
}

// Genera la lista de fechas ISO (YYYY-MM-DD) entre 2 fechas (inclusive)
export function fechasEnRango(fechaInicio, fechaFin) {
  const a = parseDate(fechaInicio);
  const b = parseDate(fechaFin);
  if (!a || !b || a > b) return [];
  const out = [];
  const d = new Date(a);
  while (d <= b) {
    out.push(toISO(d));
    d.setDate(d.getDate() + 1);
  }
  return out;
}
